import { LitElement, html, css, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

/**
 * bn-modal is the dialog shell, following bino-cli's modal pattern (scrim +
 * centered card, Escape and backdrop-click to close). It is controlled: set
 * `open` and listen for the `close` event to clear it. Slots: default (body),
 * `footer` (action row). `heading` names the dialog.
 *
 *   <bn-modal ?open=${show} heading="Delete workspace?" @close=${() => show = false}>
 *     <p>This cannot be undone.</p>
 *     <bn-button slot="footer" @click=${() => show = false}>Cancel</bn-button>
 *     <bn-button slot="footer" variant="danger" @click=${confirm}>Delete</bn-button>
 *   </bn-modal>
 */
@customElement("bn-modal")
export class BnModal extends LitElement {
  @property({ type: Boolean, reflect: true }) open = false;
  @property() heading = "";
  /** Card width in px (default 480). */
  @property({ type: Number }) width = 480;
  /** Takes the full viewport height, with an unpadded body the content sizes itself in. */
  @property({ type: Boolean, reflect: true }) fill = false;

  static override styles = css`
    :host {
      font-family: var(--bino-font-sans);
    }
    .backdrop {
      position: fixed;
      inset: 0;
      background: var(--bino-scrim);
      z-index: var(--bino-z-modal);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: var(--bino-space-md);
    }
    .modal {
      background: var(--bino-surface);
      border-radius: var(--bino-radius-lg);
      box-shadow: var(--bino-shadow-page);
      width: 100%;
      max-height: calc(100vh - 2 * var(--bino-space-xl));
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    .head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--bino-space-md);
      padding: var(--bino-space-md) var(--bino-space-lg);
      border-bottom: 1px solid var(--bino-border);
      flex-shrink: 0;
    }
    .title {
      margin: 0;
      font-size: var(--bino-font-size-md);
      font-weight: 600;
      color: var(--bino-text);
    }
    .close {
      background: none;
      border: none;
      font-size: 20px;
      line-height: 1;
      cursor: pointer;
      color: var(--bino-text-secondary);
      padding: 0 4px;
    }
    .close:hover {
      color: var(--bino-text);
    }
    .body {
      padding: var(--bino-space-lg);
      overflow-y: auto;
      color: var(--bino-text);
      font-size: var(--bino-font-size-md);
    }
    .footer {
      display: flex;
      justify-content: flex-end;
      gap: var(--bino-space-sm);
      padding: var(--bino-space-md) var(--bino-space-lg);
      border-top: 1px solid var(--bino-border);
      flex-shrink: 0;
    }
    .footer.empty {
      display: none;
    }
    :host([fill]) .modal {
      height: 100%;
    }
    :host([fill]) .body {
      flex: 1;
      min-height: 0;
      padding: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }
  `;

  private onKeydown = (e: KeyboardEvent) => {
    if (this.open && e.key === "Escape") this.close();
  };

  override connectedCallback() {
    super.connectedCallback();
    document.addEventListener("keydown", this.onKeydown);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("keydown", this.onKeydown);
  }

  private close() {
    this.dispatchEvent(new CustomEvent("close", { bubbles: true, composed: true }));
  }

  private hasFooter(): boolean {
    return this.querySelector('[slot="footer"]') !== null;
  }

  override render() {
    if (!this.open) return nothing;
    return html`
      <div class="backdrop" @click=${this.close}>
        <div
          class="modal"
          style="max-width:${this.width}px"
          role="dialog"
          aria-modal="true"
          aria-label=${this.heading || "Dialog"}
          @click=${(e: Event) => e.stopPropagation()}
        >
          <div class="head">
            <h2 class="title">${this.heading}</h2>
            <button class="close" title="Close" aria-label="Close" @click=${this.close}>
              &times;
            </button>
          </div>
          <div class="body"><slot></slot></div>
          <div class="footer ${this.hasFooter() ? "" : "empty"}">
            <slot name="footer"></slot>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bn-modal": BnModal;
  }
}
